import { Alert, Grid, Link } from "@mui/material";
import {
  ChangeEvent,
  Dispatch,
  FC,
  FocusEvent,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useState,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import { FoodItemSupplierInfo } from "client/jspPlatformExperiment";
import SpacedTextInput, {
  SpacedAutocompleteInput,
} from "components/common/ui/SpacedTextInput";
import {
  getFoodSupplierFieldAdornment,
  getFoodSupplierFieldValue,
  supplierFields as displaySupplierFields,
} from "components/food/ui/common/FoodSupplierViewSection";
import useSupplierCodeDuplicateCheck from "hooks/food/useSupplierCodeDuplicateCheck";
import Routes from "models/routes";

const SUPPLIER_OPTIONS = ["GFS", "Sysco", "GRM"];

interface FoodSupplierEditSectionProps {
  supplierInfo: FoodItemSupplierInfo;
  setSupplierInfo: Dispatch<SetStateAction<FoodItemSupplierInfo>>;
  /**
   * The id of the ingredient currently being edited, if any. Conflicts on
   * this id are suppressed so the form does not flag itself.
   */
  editingFoodId?: number;
}

const supplierFields: { label: string; name: keyof FoodItemSupplierInfo }[] = [
  ...displaySupplierFields.slice(
    0,
    displaySupplierFields.findIndex((elm) => elm.name === "url"),
  ),
  { label: "Supplier Unit Size", name: "supplier_unit_size" },
  { label: "Supplier Unit Size UOM", name: "supplier_unit_size_uom" },
  ...displaySupplierFields.slice(
    displaySupplierFields.findIndex((elm) => elm.name === "url"),
  ),
];

/** Supplier prices from the API are in cents; display and input use dollars. */
const centsToDollars = (cents: number | null | undefined): string | number =>
  cents != null ? cents / 100 : "";

const FoodSupplierEditSection: FC<FoodSupplierEditSectionProps> = ({
  supplierInfo,
  setSupplierInfo,
  editingFoodId,
}) => {
  const {
    status: productCodeCheckStatus,
    conflicts: productCodeConflicts,
    runCheck: runProductCodeCheck,
    reset: resetProductCodeCheck,
  } = useSupplierCodeDuplicateCheck(editingFoodId);

  const handleSupplierIdChange = useCallback(
    (_: SyntheticEvent, newValue: string | null) => {
      setSupplierInfo((prev) => ({
        ...prev,
        supplier_id: newValue ?? "",
      }));
    },
    [setSupplierInfo],
  );

  const handleProductCodeBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const code = event.target.value.trim();
      if (!code) {
        resetProductCodeCheck();
        return;
      }
      runProductCodeCheck(code);
    },
    [resetProductCodeCheck, runProductCodeCheck],
  );

  const [displayPackPrice, setDisplayPackPrice] = useState<string | number>(
    () => centsToDollars(supplierInfo.pack_price),
  );

  /**
   * Handle the supplier field change
   */
  const handleSupplierInfoChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let targetValue: string | number = event.target.value;

      // Adjust the value to store if needed
      switch (event.target.name) {
        case "pack_price":
          setDisplayPackPrice(targetValue);
          targetValue = Math.round((Number(targetValue) ?? 0.0) * 100);
          break;
        default:
          break;
      }

      // Changing action
      setSupplierInfo({
        ...supplierInfo,
        [event.target.name]: targetValue,
      });
    },
    [setSupplierInfo, supplierInfo],
  );

  const requiredSupplierFieldNames: (keyof FoodItemSupplierInfo)[] = [
    "supplier_id",
    "product_code",
    "product_name",
  ];

  const isRequiredFieldEmpty = (
    name: keyof FoodItemSupplierInfo,
    value: string | number | boolean,
  ): boolean =>
    requiredSupplierFieldNames.includes(name) && !String(value ?? "").trim();

  const productCodeValue = String(supplierInfo.product_code ?? "");
  const isProductCodeEmpty = isRequiredFieldEmpty(
    "product_code",
    productCodeValue,
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4} lg={3}>
        <SpacedAutocompleteInput
          label="Supplier Name"
          value={supplierInfo.supplier_id ?? ""}
          options={SUPPLIER_OPTIONS}
          shrink
          onChange={handleSupplierIdChange}
          error={
            isRequiredFieldEmpty("supplier_id", supplierInfo.supplier_id) ||
            !SUPPLIER_OPTIONS.includes(supplierInfo.supplier_id)
          }
        />
      </Grid>
      <Grid item xs={12} md={4} lg={3}>
        <SpacedTextInput
          InputLabelProps={{ shrink: true }}
          onChange={handleSupplierInfoChange}
          onBlur={handleProductCodeBlur}
          label="Product Code"
          name="product_code"
          value={productCodeValue}
          error={isProductCodeEmpty}
          required
        />
        {productCodeCheckStatus === "conflict" &&
        productCodeConflicts.length > 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Product code already used by{" "}
            {productCodeConflicts.slice(0, 3).map((conflict, index) => (
              <span key={conflict.foodId}>
                {index > 0 ? ", " : ""}
                <Link
                  component={RouterLink}
                  to={`${Routes.FOOD_INGREDIENT_EDIT}/${conflict.foodId}`}
                  target="_blank"
                  rel="noopener"
                >
                  {conflict.foodName}
                </Link>
              </span>
            ))}
            {productCodeConflicts.length > 3
              ? ` (+${productCodeConflicts.length - 3} more)`
              : ""}
            .
          </Alert>
        ) : null}
      </Grid>
      {supplierFields
        .filter((f) => f.name !== "supplier_id" && f.name !== "product_code")
        .map((supplierTextFieldProps) => {
          const value =
            supplierTextFieldProps.name === "pack_price"
              ? displayPackPrice
              : getFoodSupplierFieldValue(
                  supplierTextFieldProps.name,
                  supplierInfo,
                );
          const isEmptyRequired = isRequiredFieldEmpty(
            supplierTextFieldProps.name,
            value,
          );
          const isNumericError =
            (supplierTextFieldProps.name === "pack_size" ||
              supplierTextFieldProps.name === "unit_size" ||
              supplierTextFieldProps.name === "supplier_unit_size") &&
            (getFoodSupplierFieldValue(
              supplierTextFieldProps.name,
              supplierInfo,
            ) as number) <= 0;

          return (
            <Grid item key={supplierTextFieldProps.name} xs={12} md={4} lg={3}>
              <SpacedTextInput
                InputLabelProps={{ shrink: true }}
                onChange={handleSupplierInfoChange}
                label={supplierTextFieldProps.label}
                name={supplierTextFieldProps.name}
                value={value}
                error={isEmptyRequired || isNumericError}
                InputProps={getFoodSupplierFieldAdornment(
                  supplierTextFieldProps.name,
                  supplierInfo,
                )}
                required
              />
            </Grid>
          );
        })}
    </Grid>
  );
};

export default FoodSupplierEditSection;
